import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { subscribeMonthly } from "../lib/api";
import { toast } from "sonner";

const PLANS = [
  {
    id: "monthly",
    name: "Monthly Essentials",
    price: 999,
    period: "/month",
    features: ["24 condoms — curated mix", "Free discreet shipping", "Pause / skip anytime", "15% off any add-on"],
  },
  {
    id: "quarterly",
    name: "Quarterly Atelier",
    price: 2599,
    period: "/quarter",
    badge: "Best value · save 13%",
    features: ["72 condoms — curated mix", "Free express shipping", "Exclusive limited drops", "20% off any add-on", "Concierge SMS access"],
  },
];

export default function Subscription() {
  const [plan, setPlan] = useState("monthly");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeMonthly({ email, plan, name: name || null });
      toast.success("Subscription reserved. We'll be in touch.");
      setEmail(""); setName("");
    } catch {
      toast.error("Could not reserve. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="subscription-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 space-y-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="label-eyebrow mb-4">The BananaX Subscription</div>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.95]">
            Intimacy, on a <span className="italic text-gradient-gold">quiet schedule</span>.
          </h1>
          <p className="text-mist font-light leading-relaxed text-lg mt-6">
            A curated edit of our signatures, shipped to your door on the first of every month.
            Pause, skip, or cancel anytime — never a hard sell.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {PLANS.map((p, i) => (
            <motion.button
              key={p.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              onClick={() => setPlan(p.id)}
              className={`relative text-left p-10 border transition-all ${
                plan === p.id ? "border-gold bg-ink-900" : "border-white/10 bg-ink-950 hover:border-white/30"
              }`}
              data-testid={`plan-card-${p.id}`}
            >
              {p.badge && (
                <span className="absolute top-6 right-6 label-eyebrow text-gold border border-gold/30 px-3 py-1">
                  {p.badge}
                </span>
              )}
              <h3 className="font-serif text-3xl text-bone mb-2">{p.name}</h3>
              <div className="flex items-baseline gap-2 mb-8">
                <span className="num-display text-5xl text-gold">₹{p.price}</span>
                <span className="text-mist text-sm">{p.period}</span>
              </div>
              <ul className="space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-bone/90 font-light text-sm">
                    <Check className="w-4 h-4 text-gold stroke-[1.5] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex items-center gap-2 text-xs tracking-[0.2em] uppercase">
                <span className={`w-2 h-2 rounded-full ${plan === p.id ? "bg-gold" : "bg-mist/30"}`}></span>
                {plan === p.id ? <span className="text-gold">Selected</span> : <span className="text-mist">Select plan</span>}
              </div>
            </motion.button>
          ))}
        </div>

        <form onSubmit={submit} className="max-w-xl mx-auto bg-ink-900 border border-white/10 p-10 space-y-5" data-testid="subscription-form">
          <h3 className="font-serif text-3xl mb-2 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-gold" /> Reserve your box
          </h3>
          <p className="text-mist text-sm font-light">
            Selected plan: <span className="text-gold capitalize">{plan}</span>. We&rsquo;ll email you to complete checkout securely.
          </p>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (optional)"
            className="w-full bg-ink-950 border border-white/10 text-bone px-4 py-3 text-sm font-light focus:outline-none focus:border-gold"
            data-testid="subscription-name-input"
          />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="w-full bg-ink-950 border border-white/10 text-bone px-4 py-3 text-sm font-light focus:outline-none focus:border-gold"
            data-testid="subscription-email-input"
          />
          <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="subscription-submit-btn">
            {loading ? "Reserving..." : "Reserve my box"}
          </button>
        </form>
      </div>
    </div>
  );
}
